import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useEImzoAvailability } from '@/hooks/eimzoAvailability';

const DOT_CLASS = {
    available: 'bg-green-500',
    unavailable: 'bg-amber-500',
    checking: 'bg-muted-foreground/40 animate-pulse',
    unknown: 'bg-muted-foreground/40 animate-pulse',
};

/**
 * The ONE E-IMZO entry button (top bar, My Documents header, Projects).
 * Neutral styling — the status dot on the shield tells the truth about the
 * desktop client instead of a hardcoded green wash: green = reachable,
 * pulsing gray = probing, amber = not installed / not running. Clicking
 * always opens the setup modal, which explains the state in full.
 */
export const EImzoButton = ({ onClick, className, labelClassName }) => {
    const { t } = useTranslation(['signing']);
    const { status, version } = useEImzoAvailability();

    const title =
        status === 'available'
            ? `${t('signing:eimzo.ready')}${version ? ` · v${version.major}.${version.minor}` : ''}`
            : status === 'unavailable'
                ? t('signing:eimzo.notInstalled')
                : t('signing:eimzo.checking');

    return (
        <Button
            variant="outline"
            size="sm"
            aria-label="E-IMZO"
            title={title}
            className={cn('gap-2 shrink-0 text-foreground/80 hover:text-foreground', className)}
            onClick={onClick}
        >
            <span className="relative flex shrink-0">
                <ShieldCheck className="w-4 h-4" />
                <span
                    aria-hidden="true"
                    className={cn(
                        'absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ring-background',
                        DOT_CLASS[status] || DOT_CLASS.unknown
                    )}
                />
            </span>
            <span className={labelClassName}>E-IMZO</span>
        </Button>
    );
};

export default EImzoButton;
